import React, { useState, useEffect } from 'react';
import { signatureApi } from '../api';
import { toast } from 'sonner';
import { Lock, X, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';

const ElectronicSignature = ({ isOpen, onClose, onConfirm, title = 'Confirmação de Identidade', description }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutMessage, setLockoutMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setLoading(false);
      setRemainingAttempts(null);
      setIsLocked(false);
      setLockoutMessage('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (!password) { toast.error('Informe sua senha'); return; }
    setLoading(true);
    try {
      const res = await signatureApi.verify(password);
      setRemainingAttempts(res.data.remaining_attempts);
      toast.success(`Identidade confirmada — ${res.data.user_name}`);
      setPassword('');
      onConfirm(res.data.user_name);
    } catch (err) {
      const data = err.response?.data || {};
      const status = err.response?.status;
      const attempts = data.remaining_attempts;
      const locked = data.locked || status === 423;

      setRemainingAttempts(attempts);
      setIsLocked(locked);

      if (locked) {
        setLockoutMessage(data.detail || 'Assinatura eletrônica bloqueada temporariamente.');
        toast.error(data.detail || 'Assinatura eletrônica bloqueada.');
      } else {
        toast.error(data.detail || 'Senha incorreta. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPassword('');
    setRemainingAttempts(null);
    setIsLocked(false);
    setLockoutMessage('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70" onClick={handleClose}>
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-md p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
            <Lock size={20} className="text-blue-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold">{title}</h3>
            <p className="text-xs text-gray-400">Assinatura Eletrônica — RDC 658/2022</p>
          </div>
          <button onClick={handleClose} className="ml-auto text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {description && (
          <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded text-sm text-yellow-400">
            {description}
          </div>
        )}

        {isLocked ? (
          <div className="space-y-4">
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-center">
              <AlertTriangle size={32} className="mx-auto mb-2 text-red-400" />
              <p className="text-red-400 font-medium text-sm">{lockoutMessage}</p>
            </div>
            <button type="button" onClick={handleClose} className="btn-secondary w-full">
              Fechar
            </button>
          </div>
        ) : (
          <form onSubmit={handleConfirm} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-1.5">
                Confirme sua identidade com sua senha
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="input"
                placeholder="••••••••"
                autoFocus
                disabled={loading}
              />
            </div>

            {remainingAttempts !== null && remainingAttempts < 5 && (
              <div className={`text-xs text-center font-medium ${
                remainingAttempts <= 2 ? 'text-red-400' : 'text-yellow-400'
              }`}>
                {remainingAttempts > 0
                  ? `Tentativas restantes: ${remainingAttempts}`
                  : 'Nenhuma tentativa restante.'}
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button type="button" onClick={handleClose} className="btn-secondary" disabled={loading}>
                Cancelar
              </button>
              <button type="submit" disabled={loading} className="btn-primary flex items-center gap-2">
                {loading
                  ? <><Loader2 size={16} className="animate-spin" /> Verificando...</>
                  : <><CheckCircle size={16} /> Confirmar</>
                }
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ElectronicSignature;
